import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import axios, { AxiosResponse } from 'axios';
import { toast } from 'react-toastify';
import config from 'constants/apiConfig';
import { observability } from 'utils/observability';
import {
  selectGroupGuid,
  selectUserGuid,
} from '../../../slices/commonSelectors';
import { useSearchParams } from 'react-router';
import {
  GetSitrepsResponse,
  SitrepViewTabs,
} from 'components/Reports/reports.types';
import type { SelectedLayerForAI } from '../utils/prefetchLayerFeaturesForAI';
/**
 * The layer prefetch/token helpers reach ArcGIS and the map context. They are
 * only needed inside the refresh mutation, so load them on demand and keep this
 * hook (used by the AI drawer and reports) light to import.
 */
const loadLayerHelpers = () =>
  Promise.all([
    import('../utils/prefetchLayerFeaturesForAI'),
    import('../utils/arcGisUserTokenForAI'),
  ]).then(([prefetch, tokens]) => ({
    prefetchLayerFeaturesForAI: prefetch.prefetchLayerFeaturesForAI,
    withArcGisUserTokensForAI: tokens.withArcGisUserTokensForAI,
  }));

export const keys = {
  base: ['sitrepsV2'] as const,
  allByGroup: (group_guid: UUID) => [...keys.base, group_guid] as const,
  byId: (sitrepId: UUID | undefined) => [...keys.base, sitrepId] as const,
};

export interface SitrepImage {
  id: string;
  fileName: string;
  fileUrl: string;
  mimetype: string;
  size: string;
  createdAt: string | null;
  updatedAt: string | null;
  workspaceId: string;
  notefolderId: string | null;
  folderName: string | null;
  type: string;
  color: string | null;
  ragProcessedAt: string | null;
  ragReady: boolean;
  signedUrl: string | undefined;
  expiresAt: string | null;
  hostname: string | null;
  pathname: string | null;
}

export type DataSource = {
  id: UUID;
  description?: string;
  source: string;
};
type SitrepAutomationConfig = {
  provider: 'seerist' | 'layerStats' | 'orgEvents' | 'incidents' | 'actions';
  seeristRequest?: Record<string, unknown>;
};
type SitrepAutoRefresh = {
  enabled?: boolean;
  intervalMinutes?: number;
};
type OutputFormatStructured = {
  summary_options?: string[];
  automation?: SitrepAutomationConfig;
  /** OPORD Tasks: how many Planned actions AI should propose on generate. */
  action_count?: number;
  auto_refresh?: SitrepAutoRefresh;
};
type SectionVersion = {
  data_string: string | null;
  sitrep_section_text: string | null;
  status: string;
  timestamp: string;
  version_id: UUID;
  /** Only set on versions saved by a user; AI and automated runs leave it null. */
  updated_by_user_guid?: UUID | null;
  updated_by_user_name?: string | null;
};
export type SitrepSectionType = {
  id: UUID;
  ai_instructions: string;
  data_source: DataSource[];
  data_sources?: DataSource[];
  data_type_source: string;
  name: string;
  order: number;
  output_format: string;
  output_format_structured: OutputFormatStructured;
  automation?: SitrepAutomationConfig;
  section_id: UUID; // this is the same as the ID
  section_template_id?: UUID | null;
  /** When set, this section is nested under another section in the same sitrep. */
  parent_section_id?: UUID | null;
  /** Display-only Analytics layer rows preview the parent section. Not stored. */
  previewSectionId?: UUID | null;
  /** Version frozen at submit/approve for snapshot vs live. */
  frozen_version_id?: UUID | null;
  section_versions: SectionVersion[];
  type: string;
  versionNumber?: number;
};

export type PollSitrepResponse = GetSitrepsResponse & {
  sections: SitrepSectionType[];
};
const fetchSitrepsByGroup = async (group_guid: UUID) => {
  const response = await axios.get<GetSitrepsResponse[]>(
    `${config.apiGateway.sitrepsV2}/`,
    {
      params: { group_guid },
    }
  );
  return response.data;
};
// Hook to fetch sitreps (list of sitreps)
export const useSitrepsV2 = () => {
  const group_guid = useSelector(selectGroupGuid);

  return useQuery({
    queryKey: keys.allByGroup(group_guid),
    queryFn: () => fetchSitrepsByGroup(group_guid),
    staleTime: 0,
  });
};

export const useDraftSitrepsV2 = () => {
  const user_guid = useSelector(selectUserGuid);

  return useQuery({
    queryKey: ['sitrepsV2', 'drafts', user_guid],
    queryFn: async () => {
      const response = await axios.get<GetSitrepsResponse[]>(
        `${config.apiGateway.sitrepsV2}/drafts`,
        {
          params: { user_guid },
        }
      );
      return response.data;
    },
    staleTime: 0,
  });
};

export const useSitrepsPendingReview = (enabled = true) => {
  return useQuery({
    queryKey: ['sitrepsV2', 'pendingReview'],
    queryFn: async () => {
      const response = await axios.get<GetSitrepsResponse[]>(
        `${config.apiGateway.sitrepsV2}/pendingReview`
      );
      return response.data;
    },
    staleTime: 0,
    enabled,
  });
};

export const fetchSitrepById = async (sitrepId: UUID | undefined) => {
  const response = await axios.get<PollSitrepResponse>(
    `${config.apiGateway.sitrepsV2}/pollSitrep`,
    {
      params: { sitrep_id: sitrepId },
    }
  );
  return response.data;
};
export const useSitrepV2 = (sitrepId: UUID | undefined) => {
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: keys.byId(sitrepId),
    queryFn: () => fetchSitrepById(sitrepId),
    enabled: Boolean(sitrepId), // Only run the query if sitrepId is truthy
    refetchInterval: (query) => {
      // Access the query state via queryClient
      const queryState = queryClient.getQueryState(keys.byId(sitrepId));

      // Stop polling if the query has encountered an error
      if (queryState?.status === 'error') {
        return false;
      }

      // If data is undefined or status is undefined, continue polling
      if (!query?.state?.data?.status) {
        return 500; // Continue polling every 500ms
      }

      const sitrepData = query?.state?.data;
      const sitrepStatus = sitrepData?.status?.toLowerCase();

      // Check if any sections are still processing
      const hasProcessingSections = sitrepData?.sections?.some(
        (section: any) => {
          if (
            !section.section_versions ||
            section.section_versions.length === 0
          ) {
            return false;
          }
          const latestVersion = section.section_versions[0];
          const sectionStatus = latestVersion?.status?.toLowerCase();
          return (
            sectionStatus &&
            sectionStatus !== 'completed' &&
            sectionStatus !== 'summarized' &&
            sectionStatus !== 'error'
          );
        }
      );

      // Continue polling if sitrep is not completed OR if any sections are still processing
      if (sitrepStatus === 'error') {
        return false; // Stop polling on error
      } else if (sitrepStatus !== 'summarized' || hasProcessingSections) {
        return 500; // Continue polling every 500ms
      }

      // Stop polling only when sitrep is summarized AND all sections are completed
      return false;
    },
    refetchOnWindowFocus: false,
    refetchOnReconnect: true, // Allow refetching on reconnect in case of network issues
    refetchOnMount: false,
    retry: false, // Do not retry the query if it fails
  });
};

type InitSitRepMutationInputs = {
  sitrep_template_id?: UUID;
  sitrep_source: string;
  sitrep_source_id: UUID;
  name: string;
  description: string;
  selectedDatetime: string;
  aor_id?: UUID;
  incident_id?: UUID;
  skip_default_sections?: boolean;
  is_sitrep?: boolean;
};

type InitSitrepResponse = {
  sitrepId: UUID;
};

export const useInitSitrepV2 = () => {
  const queryClient = useQueryClient();
  const user_guid = useSelector(selectUserGuid);
  const group_guid = useSelector(selectGroupGuid);

  return useMutation<UUID, any, InitSitRepMutationInputs>({
    mutationFn: async ({
      sitrep_template_id,
      sitrep_source,
      sitrep_source_id,
      name,
      description,
      selectedDatetime,
      aor_id,
      incident_id,
      skip_default_sections,
      is_sitrep,
    }) => {
      const response = await axios.post<InitSitrepResponse>(
        `${config.apiGateway.sitrepsV2}/initSitrep`,
        {
          sitrep_template_id,
          sitrep_source,
          sitrep_source_id,
          name,
          description,
          user_guid,
          group_guid,
          selectedDatetime,
          aor_id,
          incident_id,
          skip_default_sections,
          is_sitrep,
        }
      );

      if (response.data && response.data.sitrepId) {
        return response.data.sitrepId;
      } else {
        throw new Error('Invalid response from server: sitrepId not found.');
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: keys.base });
    },
  });
};

export const useInitDraftSitrepV2 = () => {
  const [_searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const user_guid = useSelector(selectUserGuid);
  const group_guid = useSelector(selectGroupGuid);

  return useMutation<UUID, any, InitSitRepMutationInputs>({
    mutationFn: async ({
      sitrep_template_id,
      sitrep_source,
      sitrep_source_id,
      name,
      description,
      selectedDatetime,
      aor_id,
      incident_id,
      skip_default_sections,
      is_sitrep,
    }) => {
      const response = await axios.post<InitSitrepResponse>(
        `${config.apiGateway.sitrepsV2}/initDraftSitrep`,
        {
          sitrep_template_id,
          sitrep_source,
          sitrep_source_id,
          name,
          description,
          user_guid,
          group_guid,
          selectedDatetime,
          aor_id,
          incident_id,
          skip_default_sections,
          is_sitrep,
        }
      );

      if (response.data && response.data.sitrepId) {
        return response.data.sitrepId;
      } else {
        throw new Error('Invalid response from server: sitrepId not found.');
      }
    },
    onSuccess: (newSitrepId) => {
      setSearchParams(
        (searchParams) => {
          searchParams.set('sitrepTab', SitrepViewTabs.Drafts);
          searchParams.set('openReport', newSitrepId);
          return searchParams;
        },
        { replace: true }
      );

      queryClient.invalidateQueries({ queryKey: keys.base });
    },
  });
};

type UpdateSitrepMutationInputs = {
  name?: string;
  description?: string;
  sitrep_id: UUID;
  archived?: boolean;
  aor_id?: UUID;
  incident_id?: UUID;
  is_sitrep?: boolean;
  spatial_filter?: object | null;
  spatial_filter_label?: string | null;
  scope_source?: 'incident' | 'aor' | 'custom' | null;
  clear_spatial_filter?: boolean;
  layout_mode?: 'tabs' | 'document' | 'sitstat' | null;
  sitstat_layout?: Record<string, unknown> | null;
};
type UpdateSitrepOptions = Omit<UpdateSitrepMutationInputs, 'sitrep_id'> & {
  id: UUID;
};

const updateSitrep = async ({
  name,
  description,
  sitrep_id,
  archived,
  aor_id,
  incident_id,
  is_sitrep,
  spatial_filter,
  spatial_filter_label,
  scope_source,
  clear_spatial_filter,
  layout_mode,
  sitstat_layout,
}: UpdateSitrepMutationInputs) => {
  const payload: UpdateSitrepOptions = { id: sitrep_id };
  if (name !== undefined) payload.name = name;
  if (description !== undefined) payload.description = description;
  if (archived !== undefined) payload.archived = archived;
  if (aor_id !== undefined) payload.aor_id = aor_id;
  if (incident_id !== undefined) payload.incident_id = incident_id;
  if (is_sitrep !== undefined) payload.is_sitrep = is_sitrep;
  if (spatial_filter !== undefined) payload.spatial_filter = spatial_filter;
  if (spatial_filter_label !== undefined)
    payload.spatial_filter_label = spatial_filter_label;
  if (scope_source !== undefined) payload.scope_source = scope_source;
  if (clear_spatial_filter !== undefined)
    payload.clear_spatial_filter = clear_spatial_filter;
  if (layout_mode !== undefined) payload.layout_mode = layout_mode;
  if (sitstat_layout !== undefined) payload.sitstat_layout = sitstat_layout;

  const response = await axios.put<UpdateSitrepOptions>(
    `${config.apiGateway.sitrepsV2}/`,
    payload
  );
  return response.data;
};
export const useUpdateSitrepV2 = () => {
  const queryClient = useQueryClient();

  return useMutation<any, any, UpdateSitrepMutationInputs>({
    mutationFn: (vars) => updateSitrep(vars),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: keys.base });
    },
  });
};

type SitrepAutoRefreshMutationInputs = {
  sitrepId: UUID;
  enabled: boolean;
  intervalMinutes: number;
  notifyRecipients?: Array<
    { kind: 'user'; userGuid: string } | { kind: 'role'; roleId: string }
  >;
};

export const useSitrepAutoRefresh = () => {
  const queryClient = useQueryClient();

  return useMutation<
    { auto_refresh: GetSitrepsResponse['auto_refresh'] },
    unknown,
    SitrepAutoRefreshMutationInputs
  >({
    mutationFn: async ({
      sitrepId,
      enabled,
      intervalMinutes,
      notifyRecipients,
    }) => {
      const response = await axios.put(
        `${config.apiGateway.sitrepsV2}/${sitrepId}/autoRefresh`,
        { enabled, intervalMinutes, notifyRecipients: notifyRecipients ?? [] }
      );
      return response.data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: keys.base });
    },
  });
};

type RefreshSitrepMutationInputs = {
  id: UUID;
  selectedDatetime: string;
  sections?: any[];
  /**
   * Optional fresh Layers selections keyed by section id. When present, these
   * win over React Query cache (which can lag behind a just-saved
   * updateSitrepSection — Analytics Run was re-querying stale timeExtents).
   */
  layerSelectionsBySectionId?: Record<string, SelectedLayerForAI[]>;
};

export type SitrepSectionAILock = {
  sectionId: string;
  acquiredAt: string;
  expiresAt: string;
};

export type SitrepAILockedError = Error & {
  isSitrepAILocked: true;
  lock: SitrepSectionAILock;
};

export const isSitrepAILockedError = (
  err: unknown
): err is SitrepAILockedError =>
  !!err &&
  typeof err === 'object' &&
  (err as { isSitrepAILocked?: boolean }).isSitrepAILocked === true;

export const usePreviewSitrepSection = (params: {
  sitrepId: string;
  sectionId: string;
  enabled: boolean;
}) => {
  const user_guid = useSelector(selectUserGuid);
  const group_guid = useSelector(selectGroupGuid);

  return useQuery({
    queryKey: [
      ...keys.base,
      'previewSection',
      params.sitrepId,
      params.sectionId,
    ],
    queryFn: async () => {
      const response = await axios.post<{ data_string: string }>(
        `${config.apiGateway.sitrepsV2}/previewSection`,
        {
          sitrepId: params.sitrepId,
          sectionId: params.sectionId,
          user_guid,
          group_guid,
        }
      );
      return response.data;
    },
    enabled:
      params.enabled &&
      !!params.sitrepId &&
      !!params.sectionId &&
      !!user_guid &&
      !!group_guid,
    staleTime: 30_000,
  });
};

export const useRefreshSitrepV2 = () => {
  const user_guid = useSelector(selectUserGuid);
  const group_guid = useSelector(selectGroupGuid);
  const queryClient = useQueryClient();

  return useMutation<any, any, RefreshSitrepMutationInputs>({
    mutationFn: async ({
      id,
      selectedDatetime,
      sections = [],
      layerSelectionsBySectionId: layerSelectionsOverride,
    }) => {
      try {
        // Collect Layers data sources for targeted sections, then prefetch
        // feature snapshots + ArcGIS user tokens (same path as DisasterGPT chat).
        // COMS cannot query userAuth FeatureServers without these.
        const layerSelectionsBySectionId: Record<string, SelectedLayerForAI[]> =
          {};
        const cached = queryClient.getQueriesData({ queryKey: keys.base });
        const sectionLayerPairs: Array<{
          sectionId: string;
          layers: SelectedLayerForAI[];
          source: 'override' | 'cache';
        }> = [];
        const overrideIds = new Set(
          Object.keys(layerSelectionsOverride || {}).filter(
            (sectionId) =>
              Array.isArray(layerSelectionsOverride?.[sectionId]) &&
              (layerSelectionsOverride?.[sectionId]?.length || 0) > 0
          )
        );

        // Prefer caller-provided selections (fresh UI state) over cache.
        for (const sectionId of overrideIds) {
          sectionLayerPairs.push({
            sectionId,
            layers: layerSelectionsOverride![sectionId],
            source: 'override',
          });
        }

        for (const [, data] of cached) {
          const sitrep = data as
            | {
                id?: string;
                sections?: Array<{
                  id: string;
                  data_sources?: unknown;
                  parent_section_id?: string | null;
                }>;
              }
            | undefined;
          if (!sitrep || sitrep.id !== id || !Array.isArray(sitrep.sections))
            continue;
          const targetedIds = new Set(Array.isArray(sections) ? sections : []);
          const targetAll = targetedIds.size === 0;
          for (const sec of sitrep.sections) {
            const isTargeted = targetAll || targetedIds.has(sec.id);
            const isParentOfTargeted =
              !targetAll &&
              sitrep.sections.some(
                (child) =>
                  targetedIds.has(child.id) &&
                  child.parent_section_id === sec.id
              );
            if (!isTargeted && !isParentOfTargeted) {
              continue;
            }
            const sources = Array.isArray(sec.data_sources)
              ? sec.data_sources
              : [];
            for (const ds of sources as Array<{
              source?: string;
              selectedLayers?: SelectedLayerForAI[];
            }>) {
              if (ds?.source !== 'Layers' || !Array.isArray(ds.selectedLayers))
                continue;
              if (ds.selectedLayers.length === 0) continue;
              // Attach Layers to the section itself when it is targeted.
              if (isTargeted && !overrideIds.has(sec.id)) {
                sectionLayerPairs.push({
                  sectionId: sec.id,
                  layers: ds.selectedLayers,
                  source: 'cache',
                });
              }
              // Parent shell Layers should also feed targeted children
              // (OPORD Situation a–e often have empty data_sources).
              if (isParentOfTargeted) {
                for (const child of sitrep.sections) {
                  if (
                    targetedIds.has(child.id) &&
                    child.parent_section_id === sec.id &&
                    !overrideIds.has(child.id)
                  ) {
                    sectionLayerPairs.push({
                      sectionId: child.id,
                      layers: ds.selectedLayers,
                      source: 'cache',
                    });
                  }
                }
              }
            }
          }
        }

        const { prefetchLayerFeaturesForAI, withArcGisUserTokensForAI } =
          sectionLayerPairs.length > 0
            ? await loadLayerHelpers()
            : {
                prefetchLayerFeaturesForAI: undefined,
                withArcGisUserTokensForAI: undefined,
              };

        for (const { sectionId, layers } of sectionLayerPairs) {
          if (!prefetchLayerFeaturesForAI || !withArcGisUserTokensForAI) break;
          try {
            // Prefer ArcGIS user tokens for external layers — never ship large
            // complete snapshots when a token is available (avoids 413 on
            // refreshSitrep; COMS queries FeatureServer with the token).
            // Also skip snapshots when serviceUrl is present (apiKey/public
            // layers): COMS queries those server-side. Shipping Ting/FloodingBins
            // snapshots caused refreshSitrep 413 and blocked Situation generate.
            const withTokens = withArcGisUserTokensForAI(layers);
            const needsSnapshot = withTokens.filter((l) => {
              if (l.kind !== 'external') return true;
              if (l.arcgisUserToken || l.serviceUrl) return false;
              return true;
            });
            const snapshotted =
              needsSnapshot.length > 0
                ? await prefetchLayerFeaturesForAI(needsSnapshot)
                : [];
            const snapByKey = new Map(
              snapshotted.map((l) => [`${l.kind}:${l.id}`, l] as const)
            );
            layerSelectionsBySectionId[sectionId] = withTokens.map((l) => {
              if (
                l.kind === 'external' &&
                (l.arcgisUserToken || l.serviceUrl)
              ) {
                const {
                  featureSnapshot: _s,
                  featureSnapshotCoverage: _c,
                  ...rest
                } = l;
                return rest;
              }
              return snapByKey.get(`${l.kind}:${l.id}`) ?? l;
            });
          } catch (prefetchError) {
            console.warn(
              `[useRefreshSitrepV2] Layer prefetch failed for section ${sectionId}:`,
              prefetchError
            );
            // Still send token-enriched refs when possible (no snapshots).
            layerSelectionsBySectionId[sectionId] = withArcGisUserTokensForAI(
              layers
            ).map((l) => {
              if (!Array.isArray(l.featureSnapshot)) return l;
              const { featureSnapshot: _s, ...rest } = l;
              return rest;
            });
          }
        }

        // The same area polygon is attached to every section. Keep it on the
        // first copy of each layer so the JSON body stays under 1MB. The API
        // copies that polygon back onto the other sections before querying.
        {
          const seenLayerIds = new Set<string>();
          for (const layers of Object.values(layerSelectionsBySectionId)) {
            for (const layer of layers) {
              if (!layer.spatialFilter) continue;
              if (seenLayerIds.has(layer.id)) {
                delete layer.spatialFilter;
              } else {
                seenLayerIds.add(layer.id);
              }
            }
          }
        }
        const refreshBody = {
          sitrepId: id,
          user_guid,
          group_guid,
          selectedDatetime,
          sections,
          ...(Object.keys(layerSelectionsBySectionId).length > 0
            ? { layerSelectionsBySectionId }
            : {}),
        };
        const response = await axios.post(
          `${config.apiGateway.sitrepsV2}/refreshSitrep`,
          refreshBody
        );
        return response.data;
      } catch (error: any) {
        // The backend returns 409 with `error.code === 'SITREP_SECTION_AI_LOCKED'`
        // and a `lock` payload when another user is already running AI
        // generation for the same section.
        const status = error?.response?.status;
        const errorCode = error?.response?.data?.error?.code;
        const lock = error?.response?.data?.error?.details?.lock as
          | SitrepSectionAILock
          | undefined;
        if (
          status === 409 &&
          errorCode === 'SITREP_SECTION_AI_LOCKED' &&
          lock
        ) {
          const lockedError = new Error(
            'AI Generate is currently in use — please wait.'
          ) as SitrepAILockedError;
          lockedError.isSitrepAILocked = true;
          lockedError.lock = lock;
          throw lockedError;
        }
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: keys.base });
    },
  });
};

type SitrepSectionAILocksResponse = {
  locks: SitrepSectionAILock[];
};

const AI_LOCK_POLL_INTERVAL_MS = 2000;

// React Query poll
export const useSitrepSectionAILocks = (sitrepId: UUID | undefined) => {
  return useQuery({
    queryKey: [...keys.byId(sitrepId), 'sectionAILocks'] as const,
    queryFn: async () => {
      const response = await axios.get<SitrepSectionAILocksResponse>(
        `${config.apiGateway.sitrepsV2}/${sitrepId}/sectionAILocks`
      );
      const map = new Map<string, SitrepSectionAILock>();
      for (const lock of response.data?.locks ?? []) {
        map.set(lock.sectionId, lock);
      }
      return map;
    },
    enabled: Boolean(sitrepId),
    refetchInterval: AI_LOCK_POLL_INTERVAL_MS,
    refetchOnWindowFocus: false,
    refetchOnReconnect: true,
    staleTime: 0,
    retry: false,
  });
};

type CloneSitrepMutationInputs = {
  sitrepId: UUID;
};

export const useCloneSitrepV2 = () => {
  const user_guid = useSelector(selectUserGuid);
  const group_guid = useSelector(selectGroupGuid);
  const queryClient = useQueryClient();

  return useMutation<{ sitrepId: UUID }, any, CloneSitrepMutationInputs>({
    mutationFn: async ({ sitrepId }) => {
      const response = await axios.post(
        `${config.apiGateway.sitrepsV2}/cloneSitrep`,
        {
          sitrepId,
          user_guid,
          group_guid,
        }
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: keys.base });
    },
  });
};

type DuplicateSitrepAsDraftMutationInputs = {
  sitrepId: UUID;
};

export const useDuplicateSitrepAsDraftV2 = () => {
  const [_searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();

  return useMutation<
    { newSitrepId: UUID },
    any,
    DuplicateSitrepAsDraftMutationInputs
  >({
    mutationFn: async ({ sitrepId }) => {
      const response = await axios.post(
        `${config.apiGateway.sitrepsV2}/duplicateAsDraft`,
        {
          sitrepId,
        }
      );

      return response.data;
    },
    onSuccess: ({ newSitrepId }) => {
      setSearchParams(
        (searchParams) => {
          searchParams.set('sitrepTab', SitrepViewTabs.Drafts);
          searchParams.set('openReport', newSitrepId);
          return searchParams;
        },
        { replace: true }
      );

      queryClient.invalidateQueries({ queryKey: keys.base });
    },
  });
};

type UpdateSitrepSectionMutationInputs = {
  sitrepId: UUID;
  sectionId: UUID;
  // Optimistic-concurrency token: the version_id the user started editing
  // from. When omitted, the backend skips the conflict check (used for
  // settings-only updates that don't create a new version).
  baseVersionId?: UUID;
  // Skip the success toast (document view shows its own save indicator).
  silent?: boolean;
  updatedFields: {
    sectionText?: string;
    name?: string;
    type?: string;
    data_sources?: string[] | Record<string, unknown>[];
    ai_instructions?: string;
    output_format?: string;
    automation?: SitrepAutomationConfig;
    output_format_structured?: {
      summary_options?: string[];
      automation?: SitrepAutomationConfig;
      action_count?: number;
      auto_refresh?: SitrepAutoRefresh;
    };
  };
};

// Shape of the 409 conflict payload returned by COMS via
// AppError.publicDetails when another user has saved the same section.
export type SitrepSectionConflictDetails = {
  sectionId: string;
  sectionName: string | null;
  latestVersionId: string;
  latestVersionTimestamp: string;
  latestVersionText: string | null;
  latestUpdatedByUserGuid: string | null;
  latestUpdatedByUserName: string | null;
};

// Augmented Error thrown by useUpdateSitrepSectionV2 when the backend
// returns a 409 conflict. Callers should check `isSectionConflict` and
// read `conflict` to drive the resolution modal.
export type SitrepSectionConflictError = Error & {
  isSectionConflict: true;
  conflict: SitrepSectionConflictDetails;
};

export const isSitrepSectionConflictError = (
  err: unknown
): err is SitrepSectionConflictError =>
  !!err &&
  typeof err === 'object' &&
  (err as { isSectionConflict?: boolean }).isSectionConflict === true;

export const useResolveSitrepAiDraft = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (params: {
      sitrepId: string;
      sectionId: string;
      versionId: string;
      decision: 'accept' | 'reject';
      editedText?: string;
    }) => {
      const response = await axios.post(
        `${config.apiGateway.sitrepsV2}/resolveAiSectionDraft`,
        params
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: keys.base });
    },
  });
};

export const useUpdateSitrepSectionV2 = () => {
  const queryClient = useQueryClient();

  return useMutation<
    UpdateSitrepSectionMutationInputs['sectionId'],
    Error,
    UpdateSitrepSectionMutationInputs
  >({
    mutationFn: async ({
      sitrepId,
      sectionId,
      baseVersionId,
      updatedFields,
    }) => {
      try {
        const body = {
          sitrepId,
          sectionId,
          baseVersionId,
          ...updatedFields,
        };
        const response = await axios.post(
          `${config.apiGateway.sitrepsV2}/updateSitrepSection`,
          body
        );
        return response.data;
      } catch (error: any) {
        // The backend returns 409 with a structured AppError payload
        // when another user has saved a newer version. Re-throw an
        // augmented Error so callers can render the conflict modal
        // instead of falling through to the generic toast handler.
        const status = error?.response?.status;
        const conflict = error?.response?.data?.error?.details?.conflict as
          | SitrepSectionConflictDetails
          | undefined;
        if (status === 409 && conflict) {
          const conflictError = new Error(
            error?.response?.data?.error?.message ??
              'This section was updated by another user since you started editing.'
          ) as SitrepSectionConflictError;
          conflictError.isSectionConflict = true;
          conflictError.conflict = conflict;
          throw conflictError;
        }
        throw error;
      }
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: keys.base });
      if (!variables.silent) {
        toast.success('Section updated');
      }
    },
    onError: (error: any) => {
      // Conflicts are handled by the caller (modal), not via toast.
      if (isSitrepSectionConflictError(error)) return;
      const errorMessage =
        error.response?.data?.error?.message ??
        error.response?.data?.error ??
        'Failed to update section';
      toast.error(
        typeof errorMessage === 'string'
          ? errorMessage
          : 'Failed to update section'
      );
    },
  });
};

type AddSitrepSectionMutationInputs = {
  sitrepId: UUID;
  sectionTemplateId: UUID;
  selectedDatetime: string;
  sitrep_source: string;
  sitrep_source_id: UUID;
  order: number;
  aor_id?: UUID;
  parent_section_id?: string | null;
  name?: string;
  data_sources?: unknown;
  skipProcessing?: boolean;
  initialDataString?: string | null;
  initialSectionText?: string | null;
};
type AddSitrepSectionParams = AddSitrepSectionMutationInputs & {
  user_guid: UUID;
  group_guid: UUID;
};
export const addSitrepSection = async (
  vars: AddSitrepSectionMutationInputs,
  user_guid: UUID,
  group_guid: UUID
) => {
  const response = await axios.post<
    UUID,
    AxiosResponse<UUID>,
    AddSitrepSectionParams
  >(`${config.apiGateway.sitrepsV2}/addSitrepSection`, {
    ...vars,
    user_guid,
    group_guid,
  });
  return response.data;
};
export const useAddSitrepSectionV2 = () => {
  const user_guid = useSelector(selectUserGuid);
  const group_guid = useSelector(selectGroupGuid);
  const queryClient = useQueryClient();

  return useMutation<unknown, unknown, AddSitrepSectionMutationInputs>({
    mutationFn: (vars) => addSitrepSection(vars, user_guid, group_guid),
    onSuccess: (_, variables) => {
      // Invalidate the specific sitrep query to trigger a refetch
      queryClient.invalidateQueries({
        queryKey: keys.byId(variables.sitrepId),
      });
      // Also invalidate the list of sitreps for the group
      queryClient.invalidateQueries({ queryKey: keys.allByGroup(group_guid) });
    },
  });
};

type RemoveSitrepMutationInputs = {
  sitrepId: UUID;
};

export const useRemoveSitrepV2 = () => {
  const queryClient = useQueryClient();

  return useMutation<any, any, RemoveSitrepMutationInputs>({
    mutationFn: async ({ sitrepId }) => {
      const response = await axios.delete(`${config.apiGateway.sitrepsV2}/`, {
        data: { sitrep_id: sitrepId },
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: keys.base });
    },
  });
};

type RemoveSitrepSectionMutationInputs = {
  sitrepId: UUID;
  sectionId: UUID;
};

export const useRemoveSitrepSectionV2 = () => {
  const queryClient = useQueryClient();

  return useMutation<any, any, RemoveSitrepSectionMutationInputs>({
    mutationFn: async ({ sitrepId, sectionId }) => {
      const response = await axios.post(
        `${config.apiGateway.sitrepsV2}/removeSitrepSection`,
        {
          sitrepId,
          sectionId,
        }
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: keys.base });
    },
  });
};

type RemovBulkSitrepSectionMutationInputs = {
  sitrepId: UUID;
  sectionIds: UUID[];
};

export const useRemoveBulkSitrepSectionsV2 = () => {
  const queryClient = useQueryClient();

  return useMutation<any, any, RemovBulkSitrepSectionMutationInputs>({
    mutationFn: async ({ sitrepId, sectionIds }) => {
      const response = await axios.post(
        `${config.apiGateway.sitrepsV2}/removeBulkSitrepSections`,
        {
          sitrepId,
          sectionIds,
        }
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: keys.base });
    },
  });
};

type MoveSitrepSectionMutationInputs = {
  sitrepId: UUID;
  sectionId: UUID;
} & (
  | { direction: 'up' | 'down'; targetOrder?: never }
  | { targetOrder: number; direction?: never }
);

export const useMoveSitrepSectionV2 = () => {
  const queryClient = useQueryClient();

  return useMutation<
    any,
    any,
    MoveSitrepSectionMutationInputs,
    { previous?: PollSitrepResponse }
  >({
    mutationFn: async ({ sitrepId, sectionId, direction, targetOrder }) => {
      const response = await axios.post(
        `${config.apiGateway.sitrepsV2}/moveSection`,
        {
          sitrepId,
          sectionId,
          direction,
          targetOrder,
        }
      );
      return response.data;
    },
    onMutate: async (vars) => {
      if (vars.targetOrder === undefined) return {};
      await queryClient.cancelQueries({ queryKey: keys.byId(vars.sitrepId) });
      const previous = queryClient.getQueryData<PollSitrepResponse>(
        keys.byId(vars.sitrepId)
      );
      if (previous?.sections?.length) {
        const sorted = [...previous.sections].sort(
          (a, b) => (a.order ?? 0) - (b.order ?? 0)
        );
        const fromIndex = sorted.findIndex((s) => s.id === vars.sectionId);
        if (fromIndex !== -1) {
          const clamped = Math.max(
            0,
            Math.min(vars.targetOrder, sorted.length - 1)
          );
          const [moved] = sorted.splice(fromIndex, 1);
          sorted.splice(clamped, 0, moved);
          queryClient.setQueryData(keys.byId(vars.sitrepId), {
            ...previous,
            sections: sorted.map((s, i) => ({ ...s, order: i + 1 })),
          });
        }
      }
      return { previous };
    },
    onError: (_err, vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(keys.byId(vars.sitrepId), context.previous);
      }
    },
    onSettled: (_data, _err, { sitrepId }) => {
      queryClient.invalidateQueries({ queryKey: keys.byId(sitrepId) });
    },
  });
};

// =========================================================================
// Unified SITREP Designation Utilities (Frontend Computation)
// =========================================================================

/**
 * Utility function to find the designated SITREP from existing sitreps data.
 * This avoids extra network calls by computing from already-fetched data.
 *
 * @param sitreps - Array of sitreps from useSitrepsV2()
 * @param options - Either { incident_id } for incident SITREP or { aor_id } for
 *   AOR SITREP
 * @returns The designated SITREP or null if none found
 */
export const findDesignatedSitrep = (
  sitreps: GetSitrepsResponse[],
  options: { incident_id?: UUID; aor_id?: UUID }
): GetSitrepsResponse | null => {
  if (!sitreps || sitreps.length === 0) return null;

  return (
    sitreps.find((s) => {
      if (!s.is_sitrep || s.archived) return false;

      if (options.incident_id) {
        // Looking for Incident SITREP
        return s.incident_id === options.incident_id;
      } else if (options.aor_id) {
        // Looking for AOR SITREP (must have no incident)
        return s.aor_id === options.aor_id && !s.incident_id;
      }
      return false;
    }) || null
  );
};

/**
 * Hook to find the designated SITREP from existing sitreps data. Use this
 * instead of separate API calls for incident/AOR SITREP lookups.
 *
 * @param sitreps - Array of sitreps from useSitrepsV2()
 * @param incident_id - Optional incident ID for incident SITREP lookup
 * @param aor_id - Optional AOR ID for AOR SITREP lookup
 * @returns The designated SITREP or null if none found
 */
export const useDesignatedSitrep = (
  sitreps: GetSitrepsResponse[] | undefined,
  incident_id?: UUID,
  aor_id?: UUID
): GetSitrepsResponse | null => {
  return useMemo(() => {
    if (!sitreps) return null;
    return findDesignatedSitrep(sitreps, { incident_id, aor_id });
  }, [sitreps, incident_id, aor_id]);
};

/**
 * Hook to fetch the designated SITREP for a given incident. Uses frontend
 * computation from useSitrepsV2() data.
 *
 * @param incident_id - The incident ID to find the SITREP for
 * @returns Object with data property containing the designated SITREP or null
 */
export const useIncidentSitrep = (incident_id: UUID | undefined) => {
  const { data: sitreps } = useSitrepsV2();
  const designatedSitrep = useDesignatedSitrep(sitreps, incident_id, undefined);

  return { data: designatedSitrep };
};

/**
 * Hook to fetch the designated SITREP for a given AOR (when no incident is
 * associated). Uses frontend computation from useSitrepsV2() data.
 *
 * @param aor_id - The AOR ID to find the SITREP for
 * @returns Object with data property containing the designated SITREP or null
 */
export const useAorSitrep = (aor_id: UUID | undefined) => {
  const { data: sitreps } = useSitrepsV2();
  const designatedSitrep = useDesignatedSitrep(sitreps, undefined, aor_id);

  return { data: designatedSitrep };
};

// Image Hooks
export const useAddImageToSitrep = (
  sitrepId: string,
  sectionId: string,
  imageOwnerId: string
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ files }: { files: File[] }) => {
      const formData = new FormData();
      files.forEach((file) => formData.append('file', file));
      formData.append('imageOwnerId', imageOwnerId);
      formData.append('sectionId', sectionId);

      const response = await axios.post(
        config.apiGateway.sitrepImages(sitrepId),
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      return response.data;
    },
    onSuccess: (_, _variables) => {
      // Invalidate attached files query
      queryClient.invalidateQueries({
        queryKey: ['sitrepAttachedFiles', sectionId],
      });
      queryClient.invalidateQueries({
        queryKey: ['sitrepAttachedImages', sectionId],
      });
      // Also invalidate the main sitrep query to update metadata
      queryClient.invalidateQueries({
        queryKey: keys.byId(sitrepId),
      });
      toast.success('Image added successfully');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to add image');
    },
  });
};

export const useSitrepSectionImages = (sectionId: string) => {
  const isVirtualSection = sectionId.includes('::analytics-layer::');
  return useQuery({
    queryKey: ['sitrepAttachedImages', sectionId],
    queryFn: async () => {
      const response = await axios.get<SitrepImage[]>(
        config.apiGateway.sitrepSectionImages(sectionId)
      );

      return response.data;
    },
    enabled: !isVirtualSection,
  });
};

export const useRetrieveSitrepSectionImages = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (sectionId: string) => {
      const response = await axios.get<SitrepImage[]>(
        config.apiGateway.sitrepSectionImages(sectionId)
      );
      return response.data;
    },
    onSuccess: (data, variables) => {
      queryClient.setQueryData(['sitrepAttachedImages', variables], data);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to retrieve images');
    },
  });
};

export const useDeleteSitrepImage = (
  sitrepId: string,
  sectionId: string,
  imageOwnerId: string
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (imageId: string) => {
      await axios.delete(config.apiGateway.sitrepImages(sitrepId), {
        params: { imageOwnerId, imageId, sectionId },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['sitrepAttachedImages', sectionId],
      });
      toast.success('Image deleted successfully');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to delete image');
    },
  });
};

// =========================================================================
// File Attachment Hooks
// =========================================================================

export interface AttachedFile {
  id: string;
  file_name: string;
  fileUrl: string;
  mimetype: string;
  size: string;
  createdAt: string;
}

// Imperative (non-hook) fetch for a sitrep's attached files. Useful for
// one-off operations like export, where files for several sitreps need to be
// fetched inside a loop/callback rather than at component render time.
export const fetchAttachedFilesForSitrep = async (
  sitrepId: string,
  groupGuid: string
): Promise<AttachedFile[]> => {
  try {
    const response = await axios.get<AttachedFile[]>(
      config.apiGateway.getSitrepAttachedFiles(sitrepId),
      {
        params: { group_guid: groupGuid },
      }
    );
    return response.data;
  } catch (err) {
    observability.captureException(err, { sitrepId });
    throw err;
  }
};

// Query hook for attached files
export const useAttachedFiles = (sitrepId: string | undefined) => {
  const group_guid = useSelector(selectGroupGuid);

  return useQuery({
    queryKey: ['sitrepAttachedFiles', sitrepId],
    queryFn: async () => {
      if (!sitrepId) return [];
      return fetchAttachedFilesForSitrep(sitrepId, group_guid);
    },
    enabled: !!sitrepId && !!group_guid,
    staleTime: 30000,
  });
};

// Mutation hook for attaching files
export const useAttachFiles = () => {
  const queryClient = useQueryClient();
  const group_guid = useSelector(selectGroupGuid);
  const user_guid = useSelector(selectUserGuid);

  return useMutation({
    mutationFn: async (params: { sitrepId: string; fileIds: string[] }) => {
      const response = await axios.post(config.apiGateway.attachFilesToSitrep, {
        ...params,
        group_guid,
        user_guid,
      });
      return response.data;
    },
    onSuccess: (_, variables) => {
      // Invalidate attached files query
      queryClient.invalidateQueries({
        queryKey: ['sitrepAttachedFiles', variables.sitrepId],
      });
      // Also invalidate the main sitrep query to update metadata
      queryClient.invalidateQueries({
        queryKey: keys.byId(variables.sitrepId),
      });
      toast.success('Files attached successfully');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to attach files');
    },
  });
};

// Mutation hook for detaching a file
export const useDetachFile = () => {
  const queryClient = useQueryClient();
  const user_guid = useSelector(selectUserGuid);

  return useMutation({
    mutationFn: async (params: { sitrepId: string; fileId: string }) => {
      const response = await axios.post(
        config.apiGateway.detachFileFromSitrep,
        {
          ...params,
          user_guid,
        }
      );
      return response.data;
    },
    onSuccess: (_, variables) => {
      // Invalidate attached files query
      queryClient.invalidateQueries({
        queryKey: ['sitrepAttachedFiles', variables.sitrepId],
      });
      // Also invalidate the main sitrep query
      queryClient.invalidateQueries({
        queryKey: keys.byId(variables.sitrepId),
      });
      toast.success('File detached successfully');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to detach file');
    },
  });
};
